import {useContext} from 'react'

import Context from '@s-ui/react-context'
import CarAcceleration from '@adv-mt/mt-svg-icons/lib/CarAcceleration'
import CarBatteryCapacityIcon from '@adv-mt/mt-svg-icons/lib/CarBatteryCapacityOutline'
import CarConsumptionIcon from '@adv-mt/mt-svg-icons/lib/CarConsumption'
import CarElectricConsumptionIcon from '@adv-mt/mt-svg-icons/lib/CarElectricConsumptionOutline'
import CarElectricTimeFastIcon from '@adv-mt/mt-svg-icons/lib/CarElectricTimeFastOutline'
import CarElectricTimeIcon from '@adv-mt/mt-svg-icons/lib/CarElectricTimeOutline'
import CarSizeIcon from '@adv-mt/mt-svg-icons/lib/CarSize'
import CarSpeedIcon from '@adv-mt/mt-svg-icons/lib/CarSpeed'
import CarTraction4x4Icon from '@adv-mt/mt-svg-icons/lib/CarTraction4x4'
import CarTrunkIcon from '@adv-mt/mt-svg-icons/lib/CarTrunkOutline'
import CarWeightIcon from '@adv-mt/mt-svg-icons/lib/CarWeightOutline'
import CarWidthBasicIcon from '@adv-mt/mt-svg-icons/lib/CarWidthBasic'
import CilynderOutlineIcon from '@adv-mt/mt-svg-icons/lib/CilynderOutline'
import DefaultIcon from '@adv-mt/mt-svg-icons/lib/Extras'
import FuelElectricHybridIcon from '@adv-mt/mt-svg-icons/lib/FuelElectricHybrid'
import FuelOutlineIcon from '@adv-mt/mt-svg-icons/lib/FuelOutline'
import FuelStationIcon from '@adv-mt/mt-svg-icons/lib/FuelStation'
import GearManualIcon from '@adv-mt/mt-svg-icons/lib/GearManual'
import NavigationBuildingsIcon from '@adv-mt/mt-svg-icons/lib/NavigationBuildingsOutline'
import NavigationOffroadIcon from '@adv-mt/mt-svg-icons/lib/NavigationOffroadOutline'
import RoadOutlineIcon from '@adv-mt/mt-svg-icons/lib/RoadOutline'
import WeightIcon from '@adv-mt/mt-svg-icons/lib/Weight'

export const useSpecifications = ({
  acceleration,
  autonomy,
  axisDistance,
  barsDiameter,
  batteryType,
  bodyType,
  bore,
  brakeCalipers,
  chargingTime,
  chassis,
  clutch,
  consumptionAverage,
  consumptionExtraUrban,
  consumptionUrban,
  cubicCapacity,
  cycle,
  cylinders,
  cylindersDisposition,
  cylinderValves,
  distribution,
  doors,
  electricFeatures = {},
  emissions,
  forkAngle,
  frontBrake,
  frontBrakeDiameter,
  frontSuspension,
  frontSuspensionSetting,
  frontTyre,
  frontWheelTravel,
  fuelTankCapacity,
  fuelType,
  gearBox,
  gears,
  height,
  hp,
  hpRpm,
  kw,
  length,
  luggageCapacity,
  make,
  model,
  movementWeight,
  pistons,
  powerElectric,
  rearBrake,
  rearBrakeDiameter,
  rearSuspension,
  rearSuspensionSetting,
  rearTyre,
  rearWheelTravel,
  refrigeration,
  seatHeight,
  seats,
  starterSystem,
  stroke,
  supply,
  swingingArm,
  topSpeed,
  torque,
  torqueRpm,
  traction,
  trail,
  transmissionType,
  version,
  weight,
  width
}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const {HAS_JATO} = config.get('vehicles')

  const hasFullDimensions = width && height && length

  const {
    batteryCapacity,
    chargingConnector,
    consumptionAverageElectricWltp,
    range,
    electricChargingTime,
    fastChargingTime,
    maxPower,
    motorType
  } = electricFeatures

  const _getElectricChargingTime = chargingTime => {
    if (!chargingTime) return
    const {duration, chargeStart, chargeEnd} = chargingTime
    const hasAvgRange = chargeStart && chargeEnd
    const rangeLiteral = hasAvgRange ? ` (${chargeStart}% - ${chargeEnd}%)` : ''
    return `${duration} ${i18n.t('COMMON.UNIT_MINUTES')}${rangeLiteral}`
  }

  return [
    fuelType && {key: 'COMMON.FUEL', value: fuelType, icon: FuelOutlineIcon}, // Combustible
    consumptionAverage && {
      key: 'TECHNICAL_DETAILS_CONSUMPTION_AVERAGE_MODEL_DETAILS',
      value: 'TECHNICAL_DETAILS_CONSUMPTION_VALUE_MODEL_DETAILS',
      params: {consumption: consumptionAverage},
      icon: CarConsumptionIcon
    },
    luggageCapacity && {
      key: 'TECHNICAL_DETAILS_LUGGAGE_CAPACITY_MODEL_DETAILS',
      value: 'TECHNICAL_DETAILS_LUGGAGE_CAPACITY_VALUE_MODEL_DETAILS',
      params: {luggageCapacity},
      icon: CarTrunkIcon
    },
    topSpeed && {
      key: 'TECHNICAL_DETAILS_TOP_SPEED_MODEL_DETAILS',
      value: 'TECHNICAL_DETAILS_TOP_SPEED_VALUE_MODEL_DETAILS',
      params: {topSpeed},
      icon: CarSpeedIcon
    },
    hp && {
      key: 'TECHNICAL_DETAILS_HP_MODEL_DETAILS',
      value: hpRpm ? 'TECHNICAL_DETAILS_HP_RPM_VALUE_MODEL_DETAILS' : 'TECHNICAL_DETAILS_HP_VALUE_MODEL_DETAILS',
      params: hpRpm ? {hp, hpRpm} : {hp},
      icon: CilynderOutlineIcon
    },
    maxPower && {
      key: 'TECHNICAL_DETAILS_HP_ELECTRIC_MODEL_DETAILS', // Potencia máxima eléctrica
      value: 'TECHNICAL_DETAILS_HP_VALUE_MODEL_DETAILS',
      params: {hp: maxPower},
      icon: DefaultIcon
    },
    traction && {
      key: 'TECHNICAL_DETAILS_TRACTION_MODEL_DETAILS',
      value: traction,
      icon: CarTraction4x4Icon
    },
    acceleration && {
      key: 'TECHNICAL_DETAILS_ACCELERATION_MODEL_DETAILS',
      value: 'TECHNICAL_DETAILS_ACCELERATION_VALUE_MODEL_DETAILS',
      params: {acceleration},
      icon: CarAcceleration
    },
    gearBox && {
      key: 'TECHNICAL_DETAILS_GEAR_BOX_MODEL_DETAILS', // Caja de cambios
      value: 'TECHNICAL_DETAILS_GEAR_BOX_VALUE_MODEL_DETAILS',
      params: {gearBox},
      icon: GearManualIcon
    },
    supply && {
      key: 'TECHNICAL_DETAILS_SUPPLY_MODEL_DETAILS', // Alimentación
      value: supply,
      icon: DefaultIcon
    },
    seatHeight && {
      key: 'TECHNICAL_DETAILS_BODY.SEAT_HEIGHT', // Altura del asiento
      value: `${seatHeight} ${i18n.t('COMMON.UNIT_MM')}`,
      icon: DefaultIcon
    },
    brakeCalipers && {
      key: 'TECHNICAL_DETAILS_CYCLE.BRAKE_CALIPERS', // Anclaje
      value: brakeCalipers
    },
    forkAngle && {
      key: 'TECHNICAL_DETAILS_CYCLE.FORK_ANGLE', // Ángulo
      value: `${forkAngle} ${i18n.t('COMMON.UNIT_DEGREE')}`
    },
    starterSystem && {
      key: 'TECHNICAL_DETAILS_ENGINE.STARTER_SYSTEM', // Arranque
      value: starterSystem,
      icon: DefaultIcon
    },
    autonomy && {
      key: 'TECHNICAL_DETAILS_ENGINE.AUTONOMY', // Autonomía
      value: `${autonomy} ${i18n.t('COMMON.UNIT_KMS')}`
    },
    trail && {
      key: 'TECHNICAL_DETAILS_CYCLE.TRAIL', // Avance
      value: `${trail} ${i18n.t('COMMON.UNIT_MM')}`
    },
    swingingArm && {
      key: 'TECHNICAL_DETAILS_CYCLE.SWINGING_ARM', // Basculante
      value: swingingArm
    },
    gears &&
      transmissionType && {
        key: 'TECHNICAL_DETAILS_ENGINE.GEAR_BOX',
        value: `${transmissionType} ${i18n.t('TECHNICAL_DETAILS_ENGINE.GEARS_VALUE', {gears})}`,
        icon: GearManualIcon
      },
    !gears &&
      transmissionType && {
        key: 'TECHNICAL_DETAILS_ENGINE.GEAR_BOX',
        value: transmissionType,
        icon: GearManualIcon
      },
    clutch && {
      key: 'TECHNICAL_DETAILS_ENGINE.CLUTCH', // Embrague
      value: clutch
    },
    fuelTankCapacity && {
      key: 'TECHNICAL_DETAILS_FUEL_TANK_CAPACITY_MODEL_DETAILS', // Capacidad del depósito
      value: 'TECHNICAL_DETAILS_FUEL_TANK_CAPACITY_VALUE_MODEL_DETAILS',
      params: {fuelTankCapacity},
      icon: FuelStationIcon
    },
    chassis && {
      key: 'TECHNICAL_DETAILS_CYCLE.CHASSIS', // Chasis
      value: chassis
    },
    chargingTime && {
      key: 'TECHNICAL_DETAILS_ENGINE.HYBRID_CHARGING_TIME', // Tiempo de carga
      value: `${chargingTime} ${i18n.t('COMMON.UNIT_HOUR')}`,
      icon: FuelElectricHybridIcon
    },
    cylindersDisposition && {
      key: 'TECHNICAL_DETAILS_ENGINE.CYLINDERS_DISPOSITION', // Configuración
      value: cylindersDisposition,
      icon: DefaultIcon
    },
    cycle && {
      key: 'TECHNICAL_DETAILS_ENGINE.CYCLE', // Ciclo
      value: cycle
    },
    cylinders && {
      key: 'TECHNICAL_DETAILS_CYLINDERS_MODEL_DETAILS',
      value: cylinders,
      icon: DefaultIcon
    },
    cylinderValves && {
      key: 'TECHNICAL_DETAILS_ENGINE.CYLINDER_VALVES',
      value: cylinderValves,
      icon: DefaultIcon
    },
    pistons && {
      key: 'TECHNICAL_DETAILS_CYCLE.PISTONS',
      value: pistons
    },
    distribution && {
      key: 'TECHNICAL_DETAILS_DISTRIBUTION_MODEL_DETAILS',
      value: distribution,
      icon: DefaultIcon
    },
    cubicCapacity && {
      key: 'COMMON_LITRES',
      value: 'TECHNICAL_DETAILS_CUBIC_CAPACITY_VALUE_MODEL_DETAILS',
      params: {cubicCapacity},
      icon: DefaultIcon
    },
    barsDiameter && {
      key: 'TECHNICAL_DETAILS_CYCLE.BARS_DIAMETER', // Diámetro barras
      value: `${barsDiameter} ${i18n.t('COMMON.UNIT_MM')}`
    },
    axisDistance && {
      key: 'TECHNICAL_DETAILS_BODY.AXIS_DISTANCE', // Distancia entre ejes
      value: `${axisDistance} ${i18n.t('COMMON.UNIT_MM')}`
    },
    bore &&
      stroke && {
        key: 'TECHNICAL_DETAILS_ENGINE.BORE_STROKE', // Diámetro x carrera
        value: `${bore} x ${stroke} ${i18n.t('COMMON.UNIT_MM')}`
      },
    version && {key: 'COMMON.VERSION', value: version, icon: DefaultIcon},
    torqueRpm && {
      key: 'TECHNICAL_DETAILS_TORQUE_MODEL_DETAILS',
      value: torqueRpm && 'TECHNICAL_DETAILS_TORQUE_RPM_VALUE_MODEL_DETAILS',
      params: torqueRpm && {torque, torqueRpm},
      icon: DefaultIcon
    },
    frontBrake &&
      frontBrakeDiameter && {
        key: 'TECHNICAL_DETAILS_CYCLE.FRONT_BRAKE',
        value: 'TECHNICAL_DETAILS_CYCLE.FRONT_BRAKE_VALUE',
        params: {frontBrake, frontBrakeDiameter},
        icon: DefaultIcon
      },
    frontSuspension && {
      key: 'TECHNICAL_DETAILS_CYCLE.FRONT_SUSPENSION',
      value: frontSuspension
    },
    frontSuspensionSetting && {
      key: 'TECHNICAL_DETAILS_CYCLE.FRONT_SUSPENSION_SETTING',
      value: frontSuspensionSetting
    },
    frontTyre && {
      key: 'TECHNICAL_DETAILS_CYCLE.FRONT_TYRE',
      value: frontTyre
    },
    frontWheelTravel && {
      key: 'TECHNICAL_DETAILS_CYCLE.FRONT_WHEEL_TRAVEL',
      value: `${frontWheelTravel} ${i18n.t('COMMON.UNIT_MM')}`
    },
    kw && {
      key: 'TECHNICAL_DETAILS_ENGINE.KW',
      value: `${kw} ${i18n.t('COMMON.UNIT_KW')}`,
      icon: DefaultIcon
    },
    movementWeight && {
      key: 'TECHNICAL_DETAILS_BODY.MOVEMENT_WEIGHT',
      value: `${movementWeight} ${i18n.t('COMMON.UNIT_KG')}`,
      icon: DefaultIcon
    },
    rearBrake &&
      rearBrakeDiameter && {
        key: 'TECHNICAL_DETAILS_CYCLE.REAR_BRAKE',
        value: 'TECHNICAL_DETAILS_CYCLE.REAR_BRAKE_VALUE',
        params: {rearBrake, rearBrakeDiameter},
        icon: DefaultIcon
      },
    rearSuspension && {
      key: 'TECHNICAL_DETAILS_CYCLE.REAR_SUSPENSION',
      value: rearSuspension
    },
    rearSuspensionSetting && {
      key: 'TECHNICAL_DETAILS_CYCLE.REAR_SUSPENSION_SETTING',
      value: rearSuspensionSetting
    },
    rearTyre && {
      key: 'TECHNICAL_DETAILS_CYCLE.REAR_TYRE',
      value: rearTyre
    },
    rearWheelTravel && {
      key: 'TECHNICAL_DETAILS_CYCLE.REAR_WHEEL_TRAVEL',
      value: `${rearWheelTravel} ${i18n.t('COMMON.UNIT_MM')}`
    },
    refrigeration && {
      key: 'TECHNICAL_DETAILS_ENGINE.REFRIGERATION', // Refrigeración
      value: refrigeration
    },
    batteryType && {
      key: 'TECHNICAL_DETAILS_ENGINE.BATTERY_TYPE', // Tipo de batería
      value: batteryType
    },
    doors && {
      key: 'TECHNICAL_DETAILS_DOORS_MODEL_DETAILS',
      value: doors,
      icon: DefaultIcon
    },
    hasFullDimensions && {
      key: 'TECHNICAL_DETAILS_DIMENSIONS_MODEL_DETAILS',
      value: 'TECHNICAL_DETAILS_DIMENSIONS_VALUE_MODEL_DETAILS',
      params: {width, height, length},
      icon: CarWidthBasicIcon
    },
    !hasFullDimensions &&
      height && {
        key: 'TECHNICAL_DETAILS_BODY.HEIGHT',
        value: `${height} ${i18n.t('COMMON.UNIT_MM')}`,
        icon: CarSizeIcon
      },
    !hasFullDimensions &&
      length && {
        key: 'TECHNICAL_DETAILS_BODY.LENGTH',
        value: `${length} ${i18n.t('COMMON.UNIT_MM')}`,
        icon: CarSizeIcon
      },
    !hasFullDimensions &&
      width && {
        key: 'TECHNICAL_DETAILS_BODY.WIDTH',
        value: `${width} ${i18n.t('COMMON.UNIT_MM')}`,
        icon: CarSizeIcon
      },
    seats && {
      key: 'TECHNICAL_DETAILS_SEATS_MODEL_DETAILS',
      value: seats,
      icon: DefaultIcon
    },
    HAS_JATO
      ? weight && {
          key: 'TECHNICAL_DETAILS_WEIGHT_MODEL_DETAILS',
          value: 'TECHNICAL_DETAILS_WEIGHT_VALUE_MODEL_DETAILS',
          params: {weight},
          icon: CarWeightIcon
        }
      : weight && {
          key: 'TECHNICAL_DETAILS_BODY.WEIGHT_DRY',
          value: `${weight} ${i18n.t('COMMON.UNIT_KG')}`,
          icon: WeightIcon
        },
    consumptionUrban && {
      key: 'TECHNICAL_DETAILS_CONSUMPTION_URBAN_MODEL_DETAILS', // Consumo urbano
      value: 'TECHNICAL_DETAILS_CONSUMPTION_VALUE_MODEL_DETAILS',
      params: {consumption: consumptionUrban},
      icon: NavigationBuildingsIcon
    },
    consumptionExtraUrban && {
      key: 'TECHNICAL_DETAILS_CONSUMPTION_EXTRA_URBAN_MODEL_DETAILS', // Consumo extraurbano
      value: 'TECHNICAL_DETAILS_CONSUMPTION_VALUE_MODEL_DETAILS',
      params: {consumption: consumptionExtraUrban},
      icon: NavigationOffroadIcon
    },
    consumptionAverageElectricWltp && {
      key: 'TECHNICAL_DETAILS_CONSUMPTION_AVERAGE_ELECTRIC_WLTP', // Consumo medio eléctrico WLTP
      value: 'TECHNICAL_DETAILS_CONSUMPTION_VALUE_ELECTRIC_WLTP', // i.e: 18 kWh / 100 km
      params: {consumption: consumptionAverageElectricWltp},
      icon: CarElectricConsumptionIcon
    },
    range && {
      key: 'TECHNICAL_DETAILS_ENGINE.ELECTRIC_AUTONOMY_WLPT', // Autonomía eléctrica WLTP
      value: `${range} ${i18n.t('COMMON.UNIT_KMS')}`, // i.e: 320 Km
      icon: RoadOutlineIcon
    },
    batteryCapacity && {
      key: 'TECHNICAL_DETAILS_ENGINE.BATTERY_CAPACITY', // Capacidad de la batería
      value: `${batteryCapacity} ${i18n.t('COMMON.UNIT_KWH')}`,
      icon: CarBatteryCapacityIcon
    },
    motorType && {
      key: 'TECHNICAL_DETAILS_ELECTRIC_ENGINE_TYPE', // Tipo de motor eléctrico
      value: motorType, //
      icon: DefaultIcon
    },
    chargingConnector && {
      key: 'TECHNICAL_DETAILS_ELECTRIC_CONECTOR_TYPE', // Tipo de conector eléctrico
      value: chargingConnector,
      icon: DefaultIcon
    },
    electricChargingTime && {
      key: 'TECHNICAL_DETAILS_ENGINE.CHARGING_TIME', // Tiempo de carga (xx kW)
      keyParams: {
        units: electricChargingTime.maxKW ? `(${electricChargingTime.maxKW} ${i18n.t('COMMON.UNIT_KW')})` : ''
      },
      value: _getElectricChargingTime(electricChargingTime),
      icon: CarElectricTimeIcon
    },
    fastChargingTime && {
      key: 'TECHNICAL_DETAILS_ENGINE.FAST_CHARGING_TIME', // Tiempo de carga rápida
      keyParams: {
        units: fastChargingTime.maxKW ? `(${fastChargingTime.maxKW} ${i18n.t('COMMON.UNIT_KW')})` : ''
      },
      value: _getElectricChargingTime(fastChargingTime),
      icon: CarElectricTimeFastIcon
    },
    electricChargingTime?.voltage && {
      key: 'TECHNICAL_DETAILS_ENGINE.VOLTAGE_CHARGE', // Voltaje de carga
      value: `${electricChargingTime?.voltage} ${i18n.t('COMMON.UNIT_VOLTAGE')}`,
      icon: DefaultIcon
    },
    fastChargingTime?.voltage && {
      key: 'TECHNICAL_DETAILS_ENGINE.FAST_VOLTAGE_CHARGING_TIME', // Voltaje de carga rápida
      value: `${fastChargingTime?.voltage} ${i18n.t('COMMON.UNIT_VOLTAGE')}`,
      icon: DefaultIcon
    },
    electricChargingTime?.amperage && {
      key: 'TECHNICAL_DETAILS_ENGINE.AMPERAGE_CHARGE', // Amperaje de carga
      value: `${electricChargingTime?.amperage} ${i18n.t('COMMON.UNIT_AMPERES')}`,
      icon: DefaultIcon
    },
    fastChargingTime?.amperage && {
      key: 'TECHNICAL_DETAILS_ENGINE.FAST_AMPERAGE_CHARGE', // Amperaje de carga rápida
      value: `${fastChargingTime?.amperage} ${i18n.t('COMMON.UNIT_AMPERES')}`,
      icon: DefaultIcon
    },
    emissions && {
      key: 'TECHNICAL_DETAILS_EMISSIONS_MODEL_DETAILS',
      value: 'TECHNICAL_DETAILS_EMISSIONS_VALUE_MODEL_DETAILS',
      params: {emissions},
      icon: DefaultIcon
    }
  ]
}
